import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';

import './tools/DateTimeHelpers';
import { ApolloProvider } from '@apollo/client';
import client from './apolloClientVytal';
import LanguageContextProvider from './providers/LanguageContextProvider';
import ThemeContextProvider from './providers/ThemeContextProvider';

import {
  Viewer,
  ViewerContext,
  UpdateViewerContext,
} from './providers/ViewerContextProvider';
import { Base64 } from 'js-base64';
import * as translations from './assets/translations/translation.json';
import { initializeI18n } from '@/utils/i18n';

import { getGlobalContext } from '@/utils/window';
import { ColorScheme, MantineProvider } from '@mantine/core';
import { PrimeReactProvider } from 'primereact/api';
import AppShellWrapper from './components/AppShellWrapper';
import { Notifications } from '@mantine/notifications';
import { Toaster } from '@/components/ui/sonner';
import { Role, RoleToClaim } from './contants';
import 'primereact/resources/themes/bootstrap4-dark-blue/theme.css'; // preload both
import 'primereact/resources/themes/bootstrap4-light-blue/theme.css';

function isAdmin(token: any) {
  return token.iadm && token.iadm === 't';
}

function isSuperAdmin(token: any) {
  return token.isadm && token.isadm === 't';
}

function isHumanResources(token: any): boolean {
  return token.human_resources && token.human_resources === 't';
}

function isAccountManager(token: any): boolean {
  return token.account_manager && token.account_manager === 't';
}

function isOperations(token: any): boolean {
  return token.operations && token.operations === 't';
}

function isMarketing(token: any): boolean {
  return token.marketing && token.marketing === 't';
}

function isSupport(token: any): boolean {
  return token.support && token.support === 't';
}

function isFinance(token: any): boolean {
  return token.finance && token.finance === 't';
}

function isEventAccountUser(token: any): boolean {
  return token.event_account && token.event_account === 't';
}

function isEventManagerUser(token: any): boolean {
  return token.event_manager && token.event_manager === 't';
}

function isEventOwnerUser(token: any): boolean {
  return token.event_owner && token.event_owner === 't';
}

function isEventCleaningEmployeeUser(token: any): boolean {
  return token.event_cleaning_employee && token.event_cleaning_employee === 't';
}

function isColugoToken(token: any) {
  return token.aud && token.aud.length && token.aud[0] === 'colugo';
}

function getJwtToken() {
  // https://stackoverflow.com/a/15724300/2956315
  const value = `; ${document.cookie}`;
  const parts = value.split(`; jwt=`);
  if (parts && parts.length === 2) {
    const firstPart = parts.pop();
    if (firstPart) {
      const encodedToken = firstPart.split(';').shift();
      if (encodedToken) {
        const base64 = encodedToken.split('.')[1];
        const decoded = Base64.decode(base64);
        console.log({ decoded });
        return JSON.parse(decoded);
      }
    }
  }
  return {};
}

const jwtToken = getJwtToken();

const App: React.FC = () => {
  const [i18nReady, setI18nReady] = useState(false);
  const [viewer, setViewer] = useState<Viewer>({ role: '' });
  const [colorMode, setColorMode] = useState<ColorScheme>('light');

  const loadPrimeReactTheme = useCallback(() => {
    const themeMap: Record<string, string> = {
      'bootstrap4-dark-blue':
        'primereact/resources/themes/bootstrap4-dark-blue/theme.css',
      'bootstrap4-light-blue':
        'primereact/resources/themes/bootstrap4-light-blue/theme.css',
    };
    const theme =
      colorMode === 'dark' ? 'bootstrap4-dark-blue' : 'bootstrap4-light-blue';
    const existingLink = document.getElementById(
      'primereact-theme',
    ) as HTMLLinkElement;
    if (existingLink) {
      existingLink.href = themeMap[theme];
    } else {
      const link = document.createElement('link');
      link.id = 'primereact-theme';
      link.rel = 'stylesheet';
      link.href = themeMap[theme];
      document.head.appendChild(link);
    }
  }, [colorMode]);

  const handleSetViewer = useCallback((newViewer: Viewer) => {
    console.log({ viewer: newViewer });
    // @ts-ignore
    getGlobalContext().posingRole = (RoleToClaim as any)[newViewer.role];
    setViewer(newViewer);
  }, []);

  const handleSetColorMode = useCallback((mode: string) => {
    setColorMode(mode as ColorScheme);
  }, []);

  // Initialize i18n and set ready state
  useEffect(() => {
    initializeI18n(translations).then(() => setI18nReady(true));
  }, []);

  // Initialize viewer from JWT token
  useEffect(() => {
    loadPrimeReactTheme();

    let viewerRole = Role.terminalUser;
    if (
      isAdmin(jwtToken) ||
      isColugoToken(jwtToken) ||
      isSuperAdmin(jwtToken)
    ) {
      viewerRole = Role.administrator;
    } else if (isHumanResources(jwtToken)) {
      viewerRole = Role.humanResources;
    } else if (isAccountManager(jwtToken)) {
      viewerRole = Role.accountManager;
    } else if (isOperations(jwtToken)) {
      viewerRole = Role.operations;
    } else if (isMarketing(jwtToken)) {
      viewerRole = Role.marketing;
    } else if (isSupport(jwtToken)) {
      viewerRole = Role.support;
    } else if (isFinance(jwtToken)) {
      viewerRole = Role.finance;
    } else if (isEventAccountUser(jwtToken)) {
      viewerRole = Role.eventAccountUser;
    } else if (isEventManagerUser(jwtToken)) {
      viewerRole = Role.eventManager;
    } else if (isEventCleaningEmployeeUser(jwtToken)) {
      viewerRole = Role.eventCleaningEmployee;
    } else if (isEventOwnerUser(jwtToken)) {
      viewerRole = Role.eventOwner;
    }

    const newViewer: Viewer = {
      role: viewerRole,
      ...(viewerRole === Role.terminalUser
        ? {
            storeId: jwtToken.store_id,
            brandId: jwtToken.brand_id,
          }
        : {}),
      ...(viewerRole === Role.eventAccountUser
        ? {
            eventAccountId: jwtToken.event_account_id,
          }
        : {}),
      ...(viewerRole === Role.eventOwner
        ? {
            eventIds: jwtToken.event_ids.split(','),
          }
        : {}),
    };

    if (viewer.role !== newViewer.role && !(viewer as any).roleSetManually) {
      handleSetViewer(newViewer);
    }
  }, [viewer.role, handleSetViewer, loadPrimeReactTheme]);

  // Handle colorMode changes
  useEffect(() => {
    loadPrimeReactTheme();
  }, [colorMode, loadPrimeReactTheme]);

  return (
    <MantineProvider
      theme={{
        colorScheme: colorMode,
        components: {
          Input: {
            styles: () => ({
              input: {
                border: 'var(--bs-border-width) solid var(--bs-border-color)',
                backgroundColor: 'var(--bs-body-bg)',
              },
            }),
          },
          Modal: {
            defaultProps: {
              'data-use-bs': true, // To make sure the modal is styled with bootstrap
            },
          },
        },
      }}
    >
      <PrimeReactProvider>
        <Notifications position="top-right" zIndex={100} />
        <Toaster position="top-right" />
        {i18nReady && (
          <ViewerContext.Provider value={viewer}>
            <UpdateViewerContext.Provider value={handleSetViewer}>
              <ApolloProvider client={client}>
                <ThemeContextProvider
                  initialTheme="vytal"
                  setColorModeMantine={handleSetColorMode}
                >
                  <LanguageContextProvider>
                    <AppShellWrapper viewer={viewer} />
                  </LanguageContextProvider>
                </ThemeContextProvider>
              </ApolloProvider>
            </UpdateViewerContext.Provider>
          </ViewerContext.Provider>
        )}
      </PrimeReactProvider>
    </MantineProvider>
  );
};
export default App;
